import {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import {
    type IAskPredictedQuery,
    type IAskSuggestQuestion,
    NO_SUGGESTIONS,
} from "searchAsk/askSuggest";
import { countKey, useAskSuggest } from "searchAsk/useAskSuggest";

import { IconSparkle, IconX } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { FeedbackTarget } from "@btnet/services-btnet/handlers/models/feedbackTarget";
import { cn } from "@btnet/utilities-core";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";

import FeedbackWidget from "commonComponents/feedback/FeedbackWidget/FeedbackWidget";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { getSearchApiVersion } from "commonComponents/utilities/MainNavigation/AiSearch/aiSearchFlags";
import { useAiSearch } from "commonComponents/utilities/MainNavigation/AiSearch/hooks/useAiSearch";
import { useAiSearchFeedbackExtraInfo } from "commonComponents/utilities/MainNavigation/AiSearch/hooks/useAiSearchFeedbackExtraInfo";
import { useAuthToken } from "commonComponents/utilities/MainNavigation/AiSearch/hooks/useAuthToken";
import { useEntityNavigation } from "commonComponents/utilities/MainNavigation/AiSearch/hooks/useEntityNavigation";
import type { ISearchHit } from "commonComponents/utilities/MainNavigation/AiSearch/types";

import "./SearchDialog.less";

import { useWarmSearchAgent } from "./agentAnswer/useSearchAgent";
import { BrowseView, useBrowseSections } from "./browse/BrowseView";
import { addRecentItem, getRecentItems } from "./browse/recentItems";
import { getTryAskingPrompts, tryAskingRefersToOpenRecord } from "./browse/tryAsking";
import {
    describeResults,
    isRetryableSearchError,
    normalizeQuery,
    QUERY_DEBOUNCE_MS,
    resolveResultsDisplay,
    SEARCH_COMMIT_TIMEOUT_MS,
} from "./results/resultsDisplay";
import { ResultsView, useResultSections } from "./results/ResultsView";
import { useSearchSuggest } from "./results/useSearchSuggest";
import {
    type ISearchDialogSection,
    SEARCH_DIALOG_LISTBOX_ID,
    SEARCH_DIALOG_OPTION_ID_PREFIX,
} from "./rows/SearchDialogList";
import type { ISearchDialogRow } from "./rows/searchDialogRow.types";
import {
    type AskArmedBy,
    type DialogOutcome,
    type DialogTrigger,
    type GuessSource,
    type IDroppedShape,
    type NavMethod,
    type SearchPhase,
    summarizeRoutability,
    useSearchDialogLearn,
} from "./searchDialog.learn";
import { resolveAskArmedBy, resolveEnterAction, resolveView } from "./searchDialog.types";
import { SearchDialogFooter } from "./SearchDialogFooter";
import {
    attentionRows,
    SEARCH_DIALOG_SUGGESTION_ID_PREFIX,
    SEARCH_DIALOG_SUGGESTIONS_ID,
    SearchDialogSuggestions,
} from "./SearchDialogSuggestions";

const FOCUSABLE =
    "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

/**
 * No row highlighted. With Ask armed this is Ask's position, one above the
 * list, so the arrow keys reach it and Enter asks.
 */
const NO_ROW = -1;

/** Guess a predicted word inline only when it clearly leads: at least MIN_GUESS_RECORDS records and MIN_GUESS_SHARE of the candidates. */
const MIN_GUESS_RECORDS = 3;
const MIN_GUESS_SHARE = 0.5;

const flattenRows = (sections: ISearchDialogSection[]) =>
    sections.flatMap((section) => section.rows);

/** What the `Search` phase events count, for the list the user was shown. */
interface ISearchCounts {
    resultCount: number;
    renderedCount: number;
    droppedUnroutable: number;
    droppedShapes: IDroppedShape[];
}

const NO_COUNTS: ISearchCounts = {
    resultCount: 0,
    renderedCount: 0,
    droppedUnroutable: 0,
    droppedShapes: [],
};

/**
 * One search's way through the phase events, so each is sent once and the way
 * the user left it can still be reported after its state is gone. Held in a
 * ref and mutated in place; nothing renders from it.
 */
interface ITrackedSearch {
    searchId: string;
    traceId: string | null;
    query: string;
    startedAt: number | null;
    /** The committed list's counts, once it has landed. */
    counts: ISearchCounts | null;
    /** When the committed list landed, which is when its rows render. */
    rowsAt: number | null;
    completeSent: boolean;
    failed: boolean;
    /** A row from its list was opened, or its query was handed to Ask. */
    used: boolean;
    /** Closed or cleared: nothing more is reported for it. */
    left: boolean;
}

/** What goes to Ask besides the question. */
export interface IAskHandoff {
    /** The question is about the record the user has open. */
    aboutOpenRecord: boolean;
}

export interface ISearchDialogProps {
    onClose: () => void;
    /** Scopes recents so a shared machine does not mix two users' history. */
    userId: number;
    /** Passed in because `commonComponents` may not read builder flags directly. */
    builderInfo: BuilderInfo | null | undefined;
    /** Picks context-relevant browse prompts. */
    openRecordEntity: string | null;
    /** How the dialog was opened. Telemetry only — see searchDialog.learn. */
    trigger: DialogTrigger;
    /** Caller-supplied: the route is app knowledge, not commonComponents'. */
    onAskFullPage: (question: string, handoff?: IAskHandoff) => void;
}

/**
 * One centered modal hosting browse and results behind a single input.
 *
 * Search-only: a question is handed to the full-page Ask rather than answered
 * here, so Ask is a doorway and not a third view. Being a modal rather than a
 * nav dropdown is what gives the result list its width, and what obliges the
 * focus trap and scroll lock.
 */
export function SearchDialog(props: ISearchDialogProps) {
    const { onClose, userId, builderInfo, openRecordEntity, trigger, onAskFullPage } = props;

    const [query, setQuery] = useState("");
    const [userToggledAsk, setUserToggledAsk] = useState(false);
    /** The key of a row the user moved to. Null, or a row no longer listed, falls back to the default highlight. */
    const [chosenKey, setChosenKey] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const { doSearch, cancel, state } = useAiSearch(builderInfo, {
        commitTimeoutMs: SEARCH_COMMIT_TIMEOUT_MS,
    });

    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const closeFeedback = useCallback(() => {
        setIsFeedbackOpen(false);
        inputRef.current?.focus();
    }, []);
    const feedbackExtraInfo = useAiSearchFeedbackExtraInfo({
        surface: "dialog",
        searchId: state.searchId,
        traceId: state.traceId,
        query: state.searchQuery,
        results: state.committedResults,
    });

    const searchConfig = useContext(AppDefaultInfoContext)?.searchServiceConfiguration;
    const { getToken } = useAuthToken(searchConfig?.authAudience ?? "");
    const apiVersion = getSearchApiVersion(builderInfo);
    // Opening the dialog is the earliest sign a question may be coming.
    useWarmSearchAgent(apiVersion);

    const learn = useSearchDialogLearn({ surface: "dialog", apiVersion });

    // Shared with the Ask page.
    const suggest = useAskSuggest(builderInfo, {
        text: query,
        subject: null,
        enabled: true,
        owner: `${builderInfo?.builderId ?? ""}:${userId}`,
    });
    // A list for earlier text stands while the next loads only if the input
    // still starts with that text: never the empty input's under a name.
    const suggestCurrent =
        suggest.forText.trim().length === 0
            ? query.trim().length === 0
            : query.toLowerCase().startsWith(suggest.forText.toLowerCase());
    const suggestions = suggestCurrent ? suggest.suggestions : NO_SUGGESTIONS;

    // Shown while the input still starts with the text it was fetched for.
    const typeahead = useSearchSuggest(searchConfig?.apiBaseUrl ?? "", query, getToken, apiVersion);
    const typedKey = query.trim().toLowerCase();
    // Only for the text in the box: a correction of earlier text would replace
    // what has been typed since.
    const corrected =
        typedKey.length > 0 && typeahead.forQuery.toLowerCase() === typedKey
            ? typeahead.corrected
            : null;
    const names = useMemo(
        () =>
            typedKey.length > 0 &&
            typeahead.forQuery.length > 0 &&
            typedKey.startsWith(typeahead.forQuery.toLowerCase())
                ? typeahead.suggestions
                : [],
        [typedKey, typeahead.forQuery, typeahead.suggestions],
    );

    /**
     * Inline completion; the guessed suffix stays selected so typing overwrites it.
     * Priority: server whole-name completion, then a clearly-leading predicted word (MIN_GUESS_*), then the first listed name.
     */
    const prediction = useMemo((): { suffix: string; from: GuessSource } => {
        const none = { suffix: "", from: "prediction" as const };
        if (query.trim().length < 2 || query.endsWith(" ")) {
            return none;
        }
        const lower = query.toLowerCase();
        const extends_ = (text: string) =>
            text.length > query.length && text.toLowerCase().startsWith(lower);
        const pick = (text: string | undefined, from: GuessSource) =>
            text === undefined ? null : { suffix: text.slice(query.length), from };
        const whole = suggestions.completion?.text;
        // The name words the text could be finishing (one word, no more),
        // with how many records carry each: the guess is the likeliest one.
        const words = suggestions.queries.filter(
            (q) =>
                q.source === "name" &&
                q.count !== undefined &&
                extends_(q.text) &&
                !q.text.slice(query.length).includes(" "),
        );
        const wordTotal = words.reduce((sum, q) => sum + (q.count ?? 0), 0);
        const likely = (q: IAskPredictedQuery) =>
            q.source === "vocab" ||
            ((q.count ?? 0) >= MIN_GUESS_RECORDS &&
                wordTotal > 0 &&
                (q.count ?? 0) / wordTotal >= MIN_GUESS_SHARE);
        return (
            pick(whole !== undefined && extends_(whole) ? whole : undefined, "completion") ??
            pick(
                suggestions.queries.find((q) => extends_(q.text) && likely(q))?.text,
                "prediction",
            ) ??
            pick(names.find((s) => extends_(s.text))?.text, "name") ??
            none
        );
    }, [query, suggestions.completion, suggestions.queries, names]);
    // Offered only after a character is typed, never after a deletion.
    const [guessAllowed, setGuessAllowed] = useState(false);
    const inlineSuffix = guessAllowed ? prediction.suffix : "";
    useLayoutEffect(() => {
        const input = inputRef.current;
        if (input && inlineSuffix.length > 0 && document.activeElement === input) {
            input.setSelectionRange(query.length, query.length + inlineSuffix.length, "backward");
        }
    }, [query, inlineSuffix]);
    const dropGuess = useCallback(() => setGuessAllowed(false), []);

    /**
     * Bookkeeping for the two dialog-lifetime events.
     *
     * Refs rather than state throughout: none of it renders, and the close
     * event fires from an unmount cleanup that must not re-run when a counter
     * changes. `outcomeRef` is overwritten by whichever handler actually ends
     * the session, so the default of "abandoned" is what survives when nothing
     * did — which is the case worth counting.
     */
    const openedAtRef = useRef(Date.now());
    // Searches that finished, not ones started: the service already counts
    // those, and a pause in typing can start one that never finishes.
    const searchCountRef = useRef(0);
    const askCountRef = useRef(0);
    const sawResultsRef = useRef(false);
    const outcomeRef = useRef<DialogOutcome>("abandoned");
    const learnRef = useRef(learn);
    useEffect(() => {
        learnRef.current = learn;
    }, [learn]);

    const hasQuery = query.trim().length > 0;
    const liveKey = normalizeQuery(query);

    // Resolved once so Ask arming and the live region agree. Rows are the
    // committed ranking narrowed by `isRoutable`.
    const { isRoutable } = useEntityNavigation(builderInfo);
    const display = useMemo(
        () =>
            resolveResultsDisplay(
                {
                    liveKey,
                    searchQuery: state.searchQuery,
                    phase: state.phase,
                    errorKind: state.errorKind,
                    committed: state.committedResults,
                    previous: state.previousCommitted,
                },
                isRoutable,
            ),
        [
            liveKey,
            state.searchQuery,
            state.phase,
            state.errorKind,
            state.committedResults,
            state.previousCommitted,
            isRoutable,
        ],
    );

    // Read off what is on screen: a failed search offers Retry, and a pending
    // search's "nothing found" is about other text.
    const zeroResults = display.kind === "empty";
    const askArmedBy = resolveAskArmedBy({ userToggled: userToggledAsk, query, zeroResults });
    const askMode = askArmedBy !== null;

    /**
     * Once per arming, not per render: a question stays armed across every
     * keystroke after its opener, and that is one decision, not twenty.
     */
    const armedByRef = useRef<AskArmedBy | null>(null);
    // Waiting out the debounce as well as the search: either way the list on
    // screen is not yet about what is typed. A cleared input is not pending.
    const searchPending = hasQuery && (display.kind === "searching" || display.kind === "held");
    useEffect(() => {
        // Each keystroke's search disarms a nothing-found arming until it lands,
        // so a query that keeps finding nothing would re-arm on every one.
        if (askArmedBy === null && armedByRef.current === "zero-results" && searchPending) {
            return;
        }
        const wasArmed = armedByRef.current !== null;
        armedByRef.current = askArmedBy;
        if (askArmedBy === null || wasArmed) {
            return;
        }
        // Only the search for what is typed: a question arms as it is typed,
        // before the debounce starts its search, and an empty input or an
        // earlier query still holds some other search's id.
        const ownSearch = hasQuery && state.searchQuery === liveKey;
        learnRef.current.learnAskArmed(
            ownSearch
                ? { searchId: state.searchId || null, traceId: state.traceId }
                : { searchId: null, traceId: null },
            { armedBy: askArmedBy },
        );
    }, [
        askArmedBy,
        searchPending,
        hasQuery,
        liveKey,
        state.searchQuery,
        state.searchId,
        state.traceId,
    ]);

    const view = useMemo(() => resolveView({ query }), [query]);

    // Browse always lists something; results only once a routable row lands,
    // which is also when ResultsView renders the listbox. A held list is
    // hidden from assistive tech, so it does not count.
    const listShown = view.kind === "browse" || display.kind === "rows";

    // Arming Ask on an empty input has nothing to submit, so the placeholder is
    // the only feedback that the mode changed.
    const placeholder = askMode ? "Ask anything about this workspace" : "Search or ask anything";

    const routability = useMemo(
        () => summarizeRoutability(state.committedResults, isRoutable),
        [state.committedResults, isRoutable],
    );

    /**
     * The `Search` phase events (see {@link SearchPhase}), counted off the
     * committed ranking. Time-to-rows rides on the phase event.
     */
    const searchRef = useRef<ITrackedSearch | null>(null);

    const sendSearchPhase = useCallback((search: ITrackedSearch, searchPhase: SearchPhase) => {
        learnRef.current.learnSearchPhase(
            { searchId: search.searchId, traceId: search.traceId },
            {
                searchPhase,
                query: search.query,
                rankSource: "committed",
                durationMs: search.startedAt != null ? Date.now() - search.startedAt : undefined,
                timeToRowsMs:
                    search.startedAt != null && search.rowsAt != null
                        ? search.rowsAt - search.startedAt
                        : undefined,
                ...(search.counts ?? NO_COUNTS),
            },
        );
    }, []);

    /** Reports how the user left the search on screen. Refs only: the unmount calls it. */
    const leaveSearch = useCallback(() => {
        const search = searchRef.current;
        if (search === null || search.left) {
            return;
        }
        search.left = true;
        // A row opened, or the query handed to Ask, is the user moving on with
        // the search, however far its stream had got.
        if (search.used || search.failed) {
            return;
        }
        sendSearchPhase(search, search.completeSent ? "abandoned" : "interrupted");
    }, [sendSearchPhase]);

    useEffect(() => {
        if (!state.searchId) {
            return;
        }
        let search = searchRef.current;
        if (search === null || search.searchId !== state.searchId) {
            search = {
                searchId: state.searchId,
                traceId: null,
                query: state.searchQuery,
                startedAt: state.searchStartTime,
                counts: null,
                rowsAt: null,
                completeSent: false,
                failed: false,
                used: false,
                left: false,
            };
            searchRef.current = search;
        }
        // The trace event can land at any point in the stream.
        search.traceId = state.traceId;
        if (search.left) {
            return;
        }
        // Counted the way the view shows it. Finishing without a ranking shows
        // a failure with Retry, so it counts as one rather than as nothing
        // found. A stream cut after its ranking still shows the rows, so it
        // counts as finished; only one with nothing on screen failed.
        const cutAfterRows = state.phase === "error" && routability.renderedCount > 0;
        const finished = state.phase === "done" || state.phase === "empty" || cutAfterRows;
        if (
            (state.phase === "error" && !cutAfterRows) ||
            (finished && state.committedResults === null)
        ) {
            search.failed = true;
            return;
        }
        if (state.committedResults !== null) {
            search.counts = { resultCount: state.committedResults.length, ...routability };
            if (search.rowsAt === null) {
                search.rowsAt = Date.now();
                if (routability.renderedCount > 0) {
                    sawResultsRef.current = true;
                }
            }
        }
        if (!search.completeSent && finished) {
            search.completeSent = true;
            searchCountRef.current += 1;
            sendSearchPhase(search, "complete");
        }
    }, [
        state.searchId,
        state.phase,
        state.searchQuery,
        state.traceId,
        state.committedResults,
        state.searchStartTime,
        routability,
        sendSearchPhase,
    ]);

    /**
     * The mount is the episode: open on mount, close on unmount. A reload or tab
     * close skips unmount and sends no close; the open alone marks it abandoned.
     */
    useEffect(() => {
        const openedAt = openedAtRef.current;
        learnRef.current.learnDialogOpened({
            trigger,
            openRecordEntity,
            recentCount: getRecentItems(userId).length,
            promptCount: getTryAskingPrompts({ openRecordEntity }).length,
        });
        // Reads the session's counters when it runs, not their values at open.
        return () => {
            leaveSearch();
            learnRef.current.learnDialogClosed({
                outcome: outcomeRef.current,
                msOpen: Date.now() - openedAt,
                searchCount: searchCountRef.current,
                askCount: askCountRef.current,
                sawResults: sawResultsRef.current,
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount: the mount is the episode
    }, []);

    // A ref rather than autoFocus: that attribute is unreliable through a portal,
    // and jsx-a11y/no-autofocus flags it.
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    /** The key of the search last started, so an edit that normalizes to it is not re-sent. */
    const searchedKeyRef = useRef("");
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearPendingSearch = useCallback(() => {
        if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    }, []);

    useEffect(() => clearPendingSearch, [clearPendingSearch]);

    // What is on screen, not `state.errorKind`: a search that finished without
    // a ranking shows as an error while the raw state carries none.
    const searchFailed = display.kind === "error";

    const searchNow = useCallback(
        (key: string) => {
            clearPendingSearch();
            searchedKeyRef.current = key;
            doSearch(key);
        },
        [clearPendingSearch, doSearch],
    );

    const handleQueryChange = useCallback(
        (next: string) => {
            setQuery(next);
            // The row under the highlight is not the row about to be there.
            setChosenKey(null);
            clearPendingSearch();

            const key = normalizeQuery(next);
            if (key.length === 0) {
                searchedKeyRef.current = "";
                leaveSearch();
                cancel();
                return;
            }
            // A trailing space, or typing back to the query already searched.
            // Unless that search failed: editing is one way to try again.
            if (key === searchedKeyRef.current && !searchFailed) {
                return;
            }
            debounceRef.current = setTimeout(() => searchNow(key), QUERY_DEBOUNCE_MS);
        },
        [cancel, clearPendingSearch, leaveSearch, searchNow, searchFailed],
    );

    const handleRetry = useCallback(() => {
        if (liveKey.length > 0) {
            searchNow(liveKey);
        }
    }, [liveKey, searchNow]);

    // The Retry button unmounts as the search restarts, which would drop
    // focus to the page and take the dialog's keyboard handling with it.
    const handleRetryClick = useCallback(() => {
        handleRetry();
        inputRef.current?.focus();
    }, [handleRetry]);

    const canRetry = searchFailed && isRetryableSearchError(display.errorKind);

    // A search that failed for want of a connection is retried once there is one.
    const failedOffline = display.kind === "error" && display.errorKind === "network";
    useEffect(() => {
        if (!failedOffline) {
            return;
        }
        window.addEventListener("online", handleRetry);
        return () => window.removeEventListener("online", handleRetry);
    }, [failedOffline, handleRetry]);

    const announcement = useMemo(
        () =>
            view.kind === "results"
                ? describeResults(display, liveKey, corrected, isRoutable)
                : { status: "", alert: "" },
        [view.kind, display, liveKey, corrected, isRoutable],
    );

    const applyCorrection = useCallback(
        (corrected: string) => {
            learn.learnCorrectionApplied({
                searchId: state.searchId || null,
                traceId: state.traceId,
            });
            handleQueryChange(corrected);
            // The button unmounts once the corrected query has no correction
            // of its own, and would take focus out of the dialog with it.
            inputRef.current?.focus();
        },
        [learn, state.searchId, state.traceId, handleQueryChange],
    );

    const { remember } = suggest;
    /** Hands a question to the full-page Ask. */
    const handOff = useCallback(
        (question: string) => {
            askCountRef.current += 1;
            if (searchRef.current !== null) {
                searchRef.current.used = true;
            }
            outcomeRef.current = "handed_off";
            remember(question);
            onAskFullPage(question);
            onClose();
        },
        [remember, onAskFullPage, onClose],
    );

    const submitAsQuestion = useCallback(() => {
        const question = query.trim();
        if (question.length > 0) {
            handOff(question);
        }
    }, [query, handOff]);

    // Ask uses `query`: a guess still selected is not accepted.
    const shownQuery = query + inlineSuffix;

    /** A suggested question: asked on the Ask page, or an opening, put in the box to finish. */
    const askSuggested = useCallback(
        (question: string, fill: boolean) => {
            if (fill) {
                handleQueryChange(`${question} `);
                inputRef.current?.focus();
                return;
            }
            handOff(question);
        },
        [handleQueryChange, handOff],
    );

    /** Searches for a picked typeahead name. */
    const pickName = useCallback(
        (text: string, rank: number, entity: string, navMethod: NavMethod) => {
            learn.learnSuggestionPicked(
                { searchId: state.searchId || null, traceId: state.traceId },
                { rank, count: names.length, entity, navMethod },
            );
            dropGuess();
            handleQueryChange(text);
            const key = normalizeQuery(text);
            if (key.length > 0) {
                searchNow(key);
            }
            inputRef.current?.focus();
        },
        [
            learn,
            state.searchId,
            state.traceId,
            names.length,
            dropGuess,
            handleQueryChange,
            searchNow,
        ],
    );

    const handleResultOpened = useCallback(
        (
            hit: ISearchHit,
            title: string,
            subtitle: string | null,
            rank: number,
            navMethod: NavMethod,
        ) => {
            addRecentItem(userId, hit, { title, subtitle });
            outcomeRef.current = "record_opened";
            if (searchRef.current !== null) {
                searchRef.current.used = true;
            }
            learn.learnRecordOpened(
                { searchId: state.searchId || null, traceId: state.traceId },
                {
                    from: "result",
                    entity: hit.entity,
                    recordId: hit.id,
                    rank,
                    // The committed ranking is the only list the dialog renders,
                    // so a rank from anywhere else would be a different number.
                    rankSource: "committed",
                    navMethod,
                    timeToClickMs:
                        state.searchStartTime != null
                            ? Date.now() - state.searchStartTime
                            : undefined,
                    msFromOpen: Date.now() - openedAtRef.current,
                },
            );
            onClose();
        },
        [userId, onClose, learn, state.searchId, state.traceId, state.searchStartTime],
    );

    const handleRecentOpened = useCallback(
        (hit: ISearchHit, rank: number, navMethod: NavMethod) => {
            outcomeRef.current = "record_opened";
            learn.learnRecordOpened(
                { searchId: null, traceId: null },
                {
                    from: "recent",
                    entity: hit.entity,
                    recordId: hit.id,
                    rank,
                    // A recent's position is recency, not a ranking.
                    rankSource: null,
                    navMethod,
                    timeToClickMs: undefined,
                    msFromOpen: Date.now() - openedAtRef.current,
                },
            );
            onClose();
        },
        [learn, onClose],
    );

    const handleAskPrompt = useCallback(
        (prompt: string, promptIndex: number, promptCount: number) => {
            learn.learnPromptPicked({ promptIndex, promptCount });
            askCountRef.current += 1;
            outcomeRef.current = "handed_off";
            // "How is this job going?" means nothing on the Ask page unless the
            // job goes with it.
            if (tryAskingRefersToOpenRecord({ openRecordEntity })) {
                onAskFullPage(prompt, { aboutOpenRecord: true });
            } else {
                onAskFullPage(prompt);
            }
            onClose();
        },
        [onAskFullPage, onClose, learn, openRecordEntity],
    );

    const browseSections = useBrowseSections({
        userId,
        builderInfo,
        openRecordEntity,
        onAskPrompt: handleAskPrompt,
        onRecentOpened: handleRecentOpened,
    });
    const resultSections = useResultSections({
        display,
        query,
        builderInfo,
        onResultOpened: handleResultOpened,
    });

    /**
     * The rows above the view's, first in the keyboard order: while typing,
     * the names the text could be; with nothing typed, what needs attention.
     */
    const suggestionRows = useMemo((): ISearchDialogRow[] => {
        if (names.length > 0) {
            return names.map((name, index) => ({
                key: `name:${name.entityType}:${name.id}:${name.text}`,
                // Enter searches for the name, so the footer hints "search".
                kind: "search" as const,
                icon: null,
                title: name.text,
                subtitle: null,
                amount: null,
                status: null,
                trailingLabel: null,
                onSelect: (navMethod: NavMethod = "click") =>
                    pickName(name.text, index + 1, name.entityType, navMethod),
            }));
        }
        if (query.trim().length > 0) {
            return [];
        }
        return attentionRows(
            suggestions,
            suggest.counts,
            (question: IAskSuggestQuestion, rank: number, shown: number) => {
                learn.learnSuggestedQuestionPicked({
                    rank,
                    count: shown,
                    source: question.source,
                    tone: question.tone,
                    hadCount:
                        question.query !== null &&
                        typeof suggest.counts.get(countKey(question.query)) === "number",
                });
                askSuggested(question.text, question.fill);
            },
        );
    }, [names, query, suggestions, suggest.counts, learn, askSuggested, pickName]);
    const suggestionCount = suggestionRows.length;

    /** The rows the keyboard can reach: not the held list, which is on screen but inert. */
    const navigableRows = useMemo(() => {
        if (view.kind === "browse") {
            return [...suggestionRows, ...flattenRows(browseSections)];
        }
        return [...suggestionRows, ...(display.kind === "rows" ? flattenRows(resultSections) : [])];
    }, [view.kind, display.kind, suggestionRows, browseSections, resultSections]);

    /** The option id of a row by its place in the keyboard order. */
    const optionId = useCallback(
        (index: number) =>
            index < suggestionCount
                ? `${SEARCH_DIALOG_SUGGESTION_ID_PREFIX}${index}`
                : `${SEARCH_DIALOG_OPTION_ID_PREFIX}${index - suggestionCount}`,
        [suggestionCount],
    );

    // Armed, Ask holds the selection. Otherwise the first record: a prompt row
    // would make Enter fire a canned question.
    const defaultIndex = askMode ? NO_ROW : navigableRows.findIndex((row) => row.kind === "record");
    const chosenIndex =
        chosenKey === null ? -1 : navigableRows.findIndex((row) => row.key === chosenKey);
    const highlightedIndex = chosenIndex >= 0 ? chosenIndex : defaultIndex;
    const chooseRow = useCallback(
        (index: number) => setChosenKey(navigableRows[index]?.key ?? null),
        [navigableRows],
    );
    const highlightedKind = navigableRows[highlightedIndex]?.kind ?? null;
    const activeDescendant = highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined;
    // The view numbers its own rows from its first.
    const viewHighlight =
        highlightedIndex >= suggestionCount ? highlightedIndex - suggestionCount : NO_ROW;
    const enterAction = resolveEnterAction({ highlightedKind, askMode, hasQuery, canRetry });

    /**
     * cmd/ctrl+Enter is handled here and never registered as a global hotkey:
     * it is `HotkeyCommands.saveAndClose`. Hotkeys of whatever is open behind
     * the dialog (a record's Esc-to-close, its save-and-close) are kept quiet
     * by the dialog's own focus layer, not by `preventDefault` here, which
     * hotkeys-js ignores.
     */
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Escape") {
                event.preventDefault();
                // The first Escape drops an inline guess; the next closes.
                if (inlineSuffix.length > 0) {
                    dropGuess();
                    return;
                }
                onClose();
                return;
            }

            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                submitAsQuestion();
                return;
            }

            if (inlineSuffix.length > 0 && event.target === inputRef.current) {
                if (
                    (event.key === "Tab" && !event.shiftKey) ||
                    event.key === "ArrowRight" ||
                    event.key === "End"
                ) {
                    event.preventDefault();
                    const accepted = query + inlineSuffix;
                    learn.learnGuessAccepted(
                        { searchId: state.searchId || null, traceId: state.traceId },
                        {
                            key:
                                event.key === "Tab" ? "tab" : event.key === "End" ? "end" : "arrow",
                            guessFrom: prediction.from,
                            typedLength: query.length,
                            guessLength: inlineSuffix.length,
                        },
                    );
                    dropGuess();
                    handleQueryChange(accepted);
                    // The text on screen is unchanged, so the selection would
                    // stay: the caret goes to the end, as after typing it.
                    inputRef.current?.setSelectionRange(accepted.length, accepted.length);
                    return;
                }
                if (event.key === "ArrowLeft" || event.key === "Home") {
                    dropGuess();
                }
            }

            // Tab and Shift+Tab walk the typeahead's names, wrapping.
            if (event.key === "Tab" && names.length > 0 && event.target === inputRef.current) {
                event.preventDefault();
                const step = event.shiftKey ? -1 : 1;
                const onName = highlightedIndex >= 0 && highlightedIndex < names.length;
                let next: number;
                if (onName) {
                    next = (highlightedIndex + step + names.length) % names.length;
                } else {
                    next = step === 1 ? 0 : names.length - 1;
                }
                chooseRow(next);
                return;
            }

            // With a modifier, because a bare Home/End in an editable combobox
            // belongs to the caret. Not with shift, which selects text.
            const jumpsToEdge =
                (event.ctrlKey || event.metaKey) &&
                !event.shiftKey &&
                (event.key === "Home" || event.key === "End");
            if (event.key === "ArrowDown" || event.key === "ArrowUp" || jumpsToEdge) {
                const rowCount = navigableRows.length;
                if (rowCount === 0) {
                    // The caret moves and the guess is no longer selected, so
                    // it is dropped rather than left to be typed onto.
                    if (inlineSuffix.length > 0) {
                        dropGuess();
                    }
                    return;
                }
                event.preventDefault();
                let next: number;
                if (jumpsToEdge) {
                    next = event.key === "Home" ? 0 : rowCount - 1;
                } else {
                    // Clamps rather than wraps; reappearing at the other end
                    // loses the user's place. Armed, the floor is Ask itself.
                    const floor = askMode ? NO_ROW : 0;
                    const step = event.key === "ArrowDown" ? 1 : -1;
                    next = Math.max(floor, Math.min(highlightedIndex + step, rowCount - 1));
                }
                if (next === NO_ROW) {
                    setChosenKey(null);
                } else {
                    chooseRow(next);
                }
                // Here rather than on every highlight change, so a hovered row
                // never scrolls the list out from under the pointer.
                if (next !== NO_ROW) {
                    document.getElementById(optionId(next))?.scrollIntoView({ block: "nearest" });
                }
                // So the Enter that follows opens this row, not a focused button.
                if (event.target !== inputRef.current) {
                    inputRef.current?.focus();
                }
                return;
            }

            if (event.key === "Enter") {
                // A focused button owns its own Enter. Rows never hold focus,
                // so only the input's Enter is handled here.
                if (event.target !== inputRef.current) {
                    return;
                }
                const onSelect = navigableRows[highlightedIndex]?.onSelect;
                if (onSelect) {
                    event.preventDefault();
                    onSelect("keyboard");
                    return;
                }
                // Nothing highlighted, so Enter only means anything as send,
                // or as another try at a search that failed.
                if (askMode) {
                    event.preventDefault();
                    submitAsQuestion();
                } else if (canRetry) {
                    event.preventDefault();
                    handleRetry();
                }
                return;
            }

            if (event.key !== "Tab") {
                return;
            }

            const focusable = event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!first || !last) {
                return;
            }
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },
        [
            askMode,
            canRetry,
            handleRetry,
            highlightedIndex,
            navigableRows,
            onClose,
            submitAsQuestion,
            inlineSuffix,
            dropGuess,
            query,
            handleQueryChange,
            learn,
            prediction.from,
            state.searchId,
            state.traceId,
            names.length,
            optionId,
            chooseRow,
        ],
    );

    /**
     * A press on anything in the card that cannot take focus would drop focus
     * to <body>, outside the panel whose keydown carries Esc, the arrows and
     * Enter. It goes to the input instead, which is where typing belongs.
     *
     * Only while the dialog holds focus. With nothing focused, which on a
     * phone is a dismissed keyboard, focusing the input would summon it again.
     */
    const handleCardMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target instanceof Element && event.target.closest(FOCUSABLE)) {
            return;
        }
        if (!panelRef.current?.contains(document.activeElement)) {
            return;
        }
        event.preventDefault();
        inputRef.current?.focus();
    }, []);

    return createPortal(
        <FocusProvider>
            {/* The feedback form is a BTModal (z-index 100), so the dialog
                drops beneath it, and its mask, while the form is open. */}
            <div
                className={cn(
                    "fixed inset-0 bg-black opacity-30",
                    isFeedbackOpen ? "z-2" : "z-max",
                )}
                onClick={onClose}
                data-testid="search-dialog-scrim"
            />
            {/* Owns the keydown because it is the surface: there is no single
                control to hang the focus trap on, and giving the card a role
                would nest a second dialog inside this one.

                `pointer-events-none` because it spans the full width at the
                card's vertical band, where it would otherwise eat the clicks
                beside the card that belong to the scrim. */}
            <div
                ref={panelRef}
                className={cn(
                    "pointer-events-none fixed inset-0 flex justify-center sm:bottom-auto sm:top-16 sm:px-4",
                    isFeedbackOpen ? "z-2" : "z-max",
                )}
                onKeyDown={handleKeyDown}
                role="dialog"
                aria-modal="true"
                aria-label="Search or ask anything"
                data-testid="search-dialog"
            >
                <div
                    className="search-dialog-card pointer-events-auto flex h-full w-full flex-col overflow-hidden border-default bg-surface-default sm:h-auto sm:rounded-xl sm:border sm:shadow-overlay"
                    onMouseDown={handleCardMouseDown}
                    data-testid="search-dialog-card"
                >
                    <div className="flex shrink-0 items-center gap-3 pl-6 pr-4 pt-5 text-fg-default">
                        {/* Native input: BTInput registers an empty hotkey whose
                            cleanup clears the shared hotkeys-js table, and this
                            dialog unmounts on every close. */}
                        {/* eslint-disable-next-line react/forbid-elements */}
                        <input
                            ref={inputRef}
                            // BuiLink's ring. A text input matches
                            // :focus-visible however it was focused, so it
                            // shows whenever the caret is here.
                            className="min-w-0 flex-1 border-none bg-transparent font-sans text-xl text-inherit placeholder:text-fg-tertiary focus-visible:rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-50"
                            type="text"
                            placeholder={placeholder}
                            value={shownQuery}
                            onChange={(e) => {
                                // A character typed may be completed; a deletion
                                // never is (Backspace over the selected part
                                // removes just that part).
                                const inputType = (e.nativeEvent as InputEvent).inputType;
                                setGuessAllowed(
                                    typeof inputType !== "string" || inputType.startsWith("insert"),
                                );
                                handleQueryChange(e.target.value);
                            }}
                            onMouseDown={dropGuess}
                            aria-label="Search or ask a question"
                            role="combobox"
                            aria-expanded={listShown || suggestionCount > 0}
                            aria-controls={
                                suggestionCount > 0
                                    ? `${SEARCH_DIALOG_SUGGESTIONS_ID} ${SEARCH_DIALOG_LISTBOX_ID}`
                                    : SEARCH_DIALOG_LISTBOX_ID
                            }
                            aria-activedescendant={activeDescendant}
                            aria-autocomplete="both"
                            data-testid="search-dialog-input"
                        />
                        <BTButton
                            type="tertiary"
                            icon={<IconX />}
                            aria-label="Close search"
                            onClick={onClose}
                            data-testid="search-dialog-close"
                        />
                    </div>

                    <div className="flex shrink-0 items-center gap-2 border-b border-default px-4 pb-4 pt-6">
                        {/* Asks when there is something to ask; on an empty
                            input there is nothing to send, so it arms the mode. */}
                        <BTButton
                            type="tertiary"
                            selected={askMode}
                            icon={<IconSparkle />}
                            onClick={() => {
                                if (hasQuery) {
                                    submitAsQuestion();
                                    return;
                                }
                                setUserToggledAsk((on) => !on);
                                setChosenKey(null);
                                inputRef.current?.focus();
                            }}
                            aria-label={hasQuery ? `Ask: ${query.trim()}` : "Ask a question"}
                            aria-pressed={askMode}
                            data-testid="search-dialog-ask"
                        >
                            Ask
                            {/* Shown only while Enter would do the same thing. */}
                            {askMode && hasQuery && highlightedIndex === NO_ROW && (
                                <kbd className="ml-2 rounded-xs border border-default bg-surface-base px-1 text-fg-secondary">
                                    &crarr;
                                </kbd>
                            )}
                        </BTButton>
                    </div>

                    {/* The listbox inside scrolls itself; this scrolls only a
                        state with no list, should one outgrow a short sheet. */}
                    <div
                        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4"
                        data-testid="search-dialog-content"
                        data-view={view.kind}
                    >
                        <SearchDialogSuggestions
                            names={names}
                            rows={suggestionRows}
                            highlightedIndex={highlightedIndex}
                            onHoverRow={chooseRow}
                        />
                        {view.kind === "browse" && (
                            <BrowseView
                                sections={browseSections}
                                highlightedIndex={viewHighlight}
                                onHoverRow={(index) => chooseRow(index + suggestionCount)}
                            />
                        )}
                        {view.kind === "results" && (
                            <ResultsView
                                display={display}
                                sections={resultSections}
                                query={query}
                                highlightedIndex={viewHighlight}
                                onHoverRow={(index) => chooseRow(index + suggestionCount)}
                                foundSoFar={
                                    display.kind === "searching"
                                        ? state.results.filter((hit) => isRoutable(hit)).length
                                        : 0
                                }
                                corrected={corrected}
                                onApplyCorrection={applyCorrection}
                                onRetry={handleRetryClick}
                            />
                        )}
                    </div>

                    {/* Mounted for the dialog's lifetime and outside the view
                        switch: a live region that mounts along with its text
                        is often not announced. */}
                    <div
                        className="sr-only"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                        data-testid="search-dialog-status"
                    >
                        {announcement.status}
                    </div>
                    <div className="sr-only" role="alert" data-testid="search-dialog-alert">
                        {announcement.alert}
                    </div>

                    <SearchDialogFooter
                        enterAction={enterAction}
                        canAsk={hasQuery}
                        canAccept={inlineSuffix.length > 0}
                        feedback={
                            <BTButton
                                type="link"
                                onClick={() => setIsFeedbackOpen(true)}
                                data-testid="search-dialog-feedback"
                            >
                                Not finding it? Tell us
                            </BTButton>
                        }
                    />
                </div>
            </div>
            {/* Outside the panel, whose keydown would take the form's Esc and
                arrows: React carries a portal's events up its React tree. */}
            {isFeedbackOpen && (
                <FeedbackWidget
                    feedbackTarget={FeedbackTarget.AiSearch}
                    extraInfo={feedbackExtraInfo}
                    onClose={closeFeedback}
                    onSubmitSuccess={closeFeedback}
                />
            )}
        </FocusProvider>,
        document.body,
    );
}
